import * as Sentry from '@sentry/browser';
import { LitElement, html } from 'lit';
import { formatCurrency } from '../../utils/format-utils.js';
import '../icons/info-icon.js';

/**
 * @component payment-credit-card
 * @description Credit card payment card — displays fee breakdown and redirects to SimplyPaid gateway.
 *
 * @property {String} quoteReference - Quote reference number
 * @property {String} totalAmount - Formatted total (e.g. "$759.00")
 * @property {String} companyName - Customer company name
 * @property {String} contactName - Customer contact person name
 */
class PaymentCreditCard extends LitElement {
    static properties = {
        quoteReference: { type: String },
        totalAmount: { type: String },
        companyName: { type: String },
        contactName: { type: String },
        isProcessingPayment: { type: Boolean, state: true },
    };

    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.quoteReference = '';
        this.totalAmount = '$0.00';
        this.companyName = '';
        this.contactName = '';
        this.isProcessingPayment = false;
    }

    get paymentAmount() {
        return this.totalAmount.replace(/[$,]/g, '');
    }

    get transactionFee() {
        return formatCurrency(parseFloat(this.paymentAmount) * 0.025);
    }

    get totalWithFee() {
        return formatCurrency(parseFloat(this.paymentAmount) * 1.025);
    }

    _handlePayment() {
        this.isProcessingPayment = true;

        try {
            const td = `[type=ReachNZ+and+Reference=${encodeURIComponent(this.quoteReference)}+and+Name=${encodeURIComponent(this.companyName)}]`;
            const paymentUrl = `https://payment.simplypaid.com.au/reach?&cstnm=${encodeURIComponent(this.companyName)}&pymntamnt=${this.paymentAmount}&trt=${encodeURIComponent(this.quoteReference)}&td=${td}`;

            Sentry.addBreadcrumb({
                category: 'payment-credit-card',
                message: 'Credit card payment initiated',
                data: {
                    amount: this.paymentAmount,
                    fee: this.transactionFee,
                    total: this.totalWithFee,
                    customer: this.companyName,
                    gateway: 'simplypaid',
                    quote_reference: this.quoteReference,
                },
            });

            window.location.href = paymentUrl;
        } catch (error) {
            this.isProcessingPayment = false;
            Sentry.captureException(error, {
                tags: {
                    payment_method: 'credit_card',
                    quote_reference: this.quoteReference,
                },
            });
            console.error('Error initiating credit card payment:', error);
        }
    }

    render() {
        return html`
            <div class="card bg-base-200">
                <div class="card-body">
                    <h3 class="card-title text-xl mb-4">Credit Card Payment</h3>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <p class="text-sm text-gray-600 mb-1">Quote Amount</p>
                            <p class="font-bold text-lg">${this.totalAmount}</p>
                        </div>
                        <div>
                            <p class="text-sm text-gray-600 mb-1">Transaction Fee (2.5%)</p>
                            <p class="font-bold text-lg">${this.transactionFee}</p>
                        </div>
                        <div>
                            <p class="text-sm text-gray-600 mb-1">Total to Pay</p>
                            <p class="font-bold text-2xl text-peppermint">${this.totalWithFee}</p>
                        </div>
                        <div>
                            <p class="text-sm text-gray-600 mb-1">Reference</p>
                            <p class="font-bold text-lg">${this.quoteReference}</p>
                        </div>
                    </div>

                    <div class="alert alert-row alert-info mb-4">
                        <info-icon class="shrink-0"></info-icon>
                        <span class="text-sm text-left">
                            All payments made via Visa or MasterCard through our payment gateway
                            will incur a <strong>2.5%</strong> transaction fee.
                        </span>
                    </div>

                    <button
                        @click="${this._handlePayment}"
                        class="btn btn-primary"
                        ?disabled="${this.isProcessingPayment}"
                    >
                        ${this.isProcessingPayment
                            ? html`
                                  <span class="loading loading-spinner"></span>
                                  Redirecting to payment gateway...
                              `
                            : html`Proceed to Payment`}
                    </button>
                </div>
            </div>
        `;
    }
}

customElements.define('payment-credit-card', PaymentCreditCard);
